<script setup lang='ts'>
import { computed, inject, onMounted, ref } from 'vue';
import Select from '@/components/Form/Select.vue';
import { Input } from '@/components/ui/input';
import DatePicker from '@/components/DatePicker/DatePicker.vue';
import { Button } from '@/components/ui/button';
import { LazyTooltip } from '@/components/ui/tooltip';
import type { TableColumn } from '@/types/tableTypes';
import { isModelDataType, isModelArrayDataType, getFilterArraySlug } from '@/types/columnTypes';
import type { IRule } from '@/types/filterBuilderTypes';
import { Squares2X2Icon, TrashIcon, StarIcon as StarIconSolid } from '@heroicons/vue/24/solid';
import { StarIcon as StarIconOutline } from '@heroicons/vue/24/outline';
import { useFavoriteFilters } from '@/composables/useFavoriteFilters';
import {
  buildFavoriteLabel,
  dateRangeValueLabel,
  favoriteRuleColumnKey,
  isFavoritableRule,
} from '@/utils/favoriteFilters';
import ModelArrayCombobox from '@/components/FilterBuilder/ModelArrayCombobox.vue';
import FilterMultiCombobox from '@/components/FilterBuilder/FilterMultiCombobox.vue';
import FilterEnumSelect from '@/components/FilterBuilder/FilterEnumSelect.vue';
import FilterIdListInput from '@/components/FilterBuilder/FilterIdListInput.vue';
import {
  filterControlRegistry,
  resolveFilterControlOptionsSource,
} from '@/components/FilterBuilder/filterControlRegistry';
import { useFilterOptions } from '@/composables/useFilterOptions';
import { useEntityStore } from '@/stores/entityStore';
import { BOOLEAN_NO_LABEL, BOOLEAN_YES_LABEL, booleanDisplayString } from '@/utils/booleanDisplay';
import { isEndBeforeStart } from '@/utils/date';
import { idListFromRuleValue } from '@/utils/idList';
import { OPEN_RULE_COLUMN_KEY } from '@/components/FilterBuilder/openRuleColumn';

interface CriterionConfig {
  label: string;
  value: string;
  control?:
    | 'text'
    | 'number'
    | 'date'
    | 'date_range'
    | 'boolean'
    | 'none'
    | 'model_array'
    | 'multi_combobox'
    | 'single_select'
    | 'id_list';
}

const props = defineProps<{
  columns: TableColumn[];
  depth: number;
}>();

defineEmits<{
  addInnerRule: [ruleId: string]
  deleteRule: [ruleId: string]
}>();

const rule = defineModel<IRule>('rule', { required: true });

// Option+Enter adds a row and names it here, so the new row opens its column list at once.
const openRuleColumn = inject(OPEN_RULE_COLUMN_KEY, null);
const columnSelectOpen = ref(false);
onMounted(() => {
  if (openRuleColumn && openRuleColumn.value === rule.value.id) {
    openRuleColumn.value = null;
    columnSelectOpen.value = true;
  }
});


const Criterion: Record<string, CriterionConfig> = {
  isEqualTo: { label: 'is equal to', value: 'equals', control: 'text' },
  isNotEqualTo: { label: 'is not equal to', value: 'not_equals', control: 'text' },
  isGreaterThan: { label: 'is greater than', value: 'greater_than', control: 'number' },
  isLessThan: { label: 'is less than', value: 'less_than', control: 'number' },
  contains: { label: 'contains', value: 'contains', control: 'text' },
  containsAny: { label: 'contains any', value: 'contains_any', control: 'text' },
  containsAll: { label: 'contains all', value: 'contains_all', control: 'text' },
  notContainsAny: { label: 'does not contain any', value: 'not_contains_any', control: 'text' },
  notContainsAll: { label: 'does not contain all', value: 'not_contains_all', control: 'text' },
  doesNotContain: { label: 'does not contain', value: 'not_contains', control: 'text' },
  startsWith: { label: 'starts with', value: 'starts_with', control: 'text' },
  // endsWith: { label: 'ends with', value: 'ends_with', control: 'text' },
  isEmptyOrNull: { label: 'is empty', value: 'is_empty_or_null', control: 'none' },
  isNotEmptyOrNull: { label: 'is not empty', value: 'is_not_empty_or_null', control: 'none' },
  isAfter: { label: 'is after', value: 'greater_than', control: 'date' },
  isBefore: { label: 'is before', value: 'less_than', control: 'date' },
  // Both ends count as inside the range. The value is `['2026-11-01', '2026-11-07']`.
  isBetween: { label: 'is between', value: 'between', control: 'date_range' },
  // NOTE: "Is" does NOT reach rows with no value recorded. It compiles to an
  // Elasticsearch `term` query (ElasticSearchField::isCriterionTransform), and
  // ES does not index nulls, so "Is No" excludes them. That is what the empty
  // operators on nullable booleans are for — they are not redundant with this.
  is: { label: 'Is', value: 'is', control: 'boolean' },
  // Free-entry id lists. The backend sees a numeric field and compiles these to
  // an Elasticsearch `terms` query (ElasticSearchField::buildNumericContainsAnyQuery),
  // casting every entry to an integer on the way. That cast is why they are only
  // offered on id columns: on a decimal column 2.5 would silently become 2.
  isOneOfIds: { label: 'is one of', value: 'contains_any', control: 'id_list' },
  isNoneOfIds: { label: 'is none of', value: 'not_contains_any', control: 'id_list' },
};

/**
 * Whether a column holds whole-number ids, so an id list is a safe thing to
 * filter it by. models.json types every integer and decimal alike as `number`,
 * so the name is the only signal here. `id` and `*_id` are mapped as integers
 * in every searchable model's elasticMappings.
 */
function isIntegerIdColumn(column: TableColumn): boolean {
  return column.dataType === 'number' && (column.key === 'id' || column.key.endsWith('_id'));
}


const criteriaMapping: Record<string, CriterionConfig[]> = {
  number: [
    { ...Criterion.isEqualTo, control: 'number' },
    { ...Criterion.isNotEqualTo, control: 'number' },
    Criterion.isGreaterThan,
    Criterion.isLessThan,
  ],
  // "contains" leads because it is the operator people want for text, and a new
  // rule takes the first criterion in this list as its default.
  string: [
    Criterion.contains,
    Criterion.isEqualTo,
    Criterion.isNotEqualTo,
    Criterion.doesNotContain,
    Criterion.startsWith,
    // Criterion.endsWith,
    Criterion.isEmptyOrNull,
    Criterion.isNotEmptyOrNull,
  ],
  Date: [
    { ...Criterion.isEqualTo, control: 'date' },
    { ...Criterion.isNotEqualTo, control: 'date' },
    Criterion.isAfter,
    Criterion.isBefore,
  ],
  // The empty operators are NOT listed here: they are only correct for a
  // NULLABLE boolean, so getAvailableCriteriaForColumn adds them behind the
  // same isNullable gate that number and Date columns use.
  boolean: [
    Criterion.is
  ],
  relation: [
    Criterion.contains,
    Criterion.doesNotContain,
    Criterion.isEmptyOrNull,
    Criterion.isNotEmptyOrNull,
  ],
  'any[]': [
    Criterion.contains,
    Criterion.doesNotContain,
    Criterion.isEmptyOrNull,
    Criterion.isNotEmptyOrNull,
  ],
};

// The plain number operators with the id-list pair after "is not equal to".
// Built from criteriaMapping.number so the two lists cannot drift apart.
const idColumnCriteria: CriterionConfig[] = [
  ...criteriaMapping.number.slice(0, 2),
  Criterion.isOneOfIds,
  Criterion.isNoneOfIds,
  ...criteriaMapping.number.slice(2),
];

interface ColumnDefault {
  pattern: (key: string, header: string) => boolean;
  criterion: string;
  value: any;
}

const columnDefaults: ColumnDefault[] = [
  {
    pattern: (columnKey, columnHeader) =>
      columnKey.toLowerCase().includes('archived') ||
      columnHeader.toLowerCase().includes('archived'),
    criterion: 'is',
    value: false
  }
];

function getColumnDefaults(columnKey: string, columnHeader: string): ColumnDefault | null {
  for (const defaultConfig of columnDefaults) {
    if (defaultConfig.pattern(columnKey, columnHeader)) {
      return defaultConfig;
    }
  }
  return null;
}

function filterRegistryCriteriaForColumn(column: TableColumn | undefined): CriterionConfig[] {
  const slug = resolveFilterControlOptionsSource(column);
  if (!slug) return [];
  const entry = filterControlRegistry[slug];
  if (!entry) return [];
  // Pull the per-criterion control from the registry so a single entry can
  // expose both scalar ("is") and array ("is one of") operators with the right
  // value-input widget for each.
  return entry.criteria.map((registryCriterion) => ({
    label: registryCriterion.label,
    value: registryCriterion.value,
    control: registryCriterion.control,
  }));
}

/**
 * The operators for a date on one nested schedule. The backend compiler refuses text
 * operators on a schedule date (NestedRuleGroupCompiler::DATE_CRITERIA in tabasco).
 * The backend accepts "is between" on any column. It is offered only on the schedule dates,
 * because that is where it was asked for.
 */
const perScheduleDateCriteria: CriterionConfig[] = [
  { ...Criterion.isEqualTo, control: 'date' },
  Criterion.isAfter,
  Criterion.isBefore,
  Criterion.isBetween,
  Criterion.isEmptyOrNull,
];

function getAvailableCriteriaForColumn(column: TableColumn): CriterionConfig[] {
  if (column.perScheduleNested && column.dataType === 'Date') {
    return perScheduleDateCriteria;
  }
  const fromRegistry = filterRegistryCriteriaForColumn(column);
  if (fromRegistry.length > 0) {
    // Attaching a picker does not remove the column's own null state, so a nullable
    // column keeps "is empty" / "is not empty" here for exactly the reason the
    // scalar branch below adds them: a `term` query never matches an unindexed
    // null, so without these the rows holding no value cannot be reached by any
    // filter. Bucket Type is the case that made this bite -- bucket_type_id is
    // NULL until the trigger backfill reaches the row, and listing those rows is
    // the first thing anyone wants during a backfill.
    return column.isNullable
      ? [...fromRegistry, Criterion.isEmptyOrNull, Criterion.isNotEmptyOrNull]
      : fromRegistry;
  }

  // Check for model arrays
  if (column && isModelArrayDataType(column.dataType)) {
    return [Criterion.containsAny, Criterion.containsAll, Criterion.notContainsAny, Criterion.notContainsAll];
  }

  if (column?.dataType) {
    const dataType = column.dataType;
    if (dataType in criteriaMapping) {
      const base = isIntegerIdColumn(column)
        ? idColumnCriteria
        : criteriaMapping[dataType as keyof typeof criteriaMapping];
      // Add null/empty options only for columns marked nullable in the model (per backend API).
      //
      // `boolean` is gated here for the same reason number and Date are, and the
      // gate is load-bearing. A nullable flag is three-state — yes, no, and
      // no-value-recorded — and "Is" compiles to an Elasticsearch `term` query,
      // which never matches an unindexed null, so without these operators those
      // rows cannot be reached by any filter. But on a NON-nullable boolean the
      // field is present on every document, so "is empty" (`must_not{exists}`)
      // always returns nothing and "is not empty" (`exists`) always returns
      // everything. Offering them there is a worse bug than the one they fix:
      // 64 of the 105 searchable boolean columns are non-nullable, including
      // every `is_archived`.
      if (column.isNullable && (dataType === 'number' || dataType === 'Date' || dataType === 'boolean')) {
        return [...base, Criterion.isEmptyOrNull, Criterion.isNotEmptyOrNull];
      }
      return base;
    }
    if (isModelDataType(dataType)) {
      return criteriaMapping.relation;
    }
  }

  return [Criterion.is];
}

function isCriterionValidForColumn(criterion: string, column: TableColumn): boolean {
  const availableCriteria = getAvailableCriteriaForColumn(column);
  return availableCriteria.some(
    (criterionOption) => criterionOption.value === criterion
  );
}

const column = computed({
  get: () => favoriteRuleColumnKey(rule.value),
  set: (selectedColumnKey) => {
    const selectedColumn = props.columns.find((tableColumn) => tableColumn.key === selectedColumnKey);
      if (selectedColumn) {
      // If the column contains a period, split it, the first part is the column the second is the field
      if (selectedColumnKey.includes('.')) {
        const columnParts = selectedColumnKey.split('.');
        rule.value.column = columnParts[0];
        rule.value.field = columnParts[1];
      } else {
        rule.value.column = selectedColumn.key; // Use key instead of header
        rule.value.field = null;
      }
      rule.value.value = null;
      
      // Check if current criterion is valid for the new column
      const currentCriterion = rule.value.criterion;
      const isCriterionValid = currentCriterion && isCriterionValidForColumn(currentCriterion, selectedColumn);

      // Reset criterion if it's not valid for the new column
      if (!isCriterionValid) {
        rule.value.criterion = '';
      }

      // Check for column defaults first
      const columnDefault = getColumnDefaults(selectedColumn.key, selectedColumn.header);
      let defaultCriterionApplied = false;
      let defaultValueApplied = false;
      
      if (columnDefault) {
        const criterionToCheck = rule.value.criterion;
        const currentValue = rule.value.value;
        
        // Apply default criterion if empty
        if (!criterionToCheck || criterionToCheck === '') {
          rule.value.criterion = columnDefault.criterion;
          defaultCriterionApplied = true;
        }
        
        // Apply default value if empty
        if (currentValue === null || currentValue === undefined || currentValue === '') {
          // Ensure value type matches the expected type (boolean, number, etc.)
          rule.value.value = columnDefault.value;
          defaultValueApplied = true;
        }
      }
      
      // Auto-select the first registry criterion when the column uses a registry
      // multi-combobox (e.g. severity defaults to "is" / equals; party IDs
      // default to "contains any"). Falls back to "contains_any" for legacy
      // entries that don't expose an explicit first criterion.
      const registryCriteriaForColumn = filterRegistryCriteriaForColumn(selectedColumn);
      if (registryCriteriaForColumn.length > 0) {
        const firstRegistryCriterion = registryCriteriaForColumn[0];
        rule.value.criterion = firstRegistryCriterion.value;
        rule.value.value = firstRegistryCriterion.control === 'single_select' ? null : [];
      } else if (selectedColumn && isModelArrayDataType(selectedColumn.dataType)) {
        // Auto-select "contains any" criterion when a model array field is selected
        rule.value.criterion = 'contains_any';
        rule.value.value = [];
      } else {
        // Auto-select first criterion option if criterion is empty (and wasn't set by defaults)
        const currentCriterion = rule.value.criterion;
        let selectedCriterionConfig: CriterionConfig | undefined;
        
        if ((!currentCriterion || currentCriterion === '') && !defaultCriterionApplied) {
          // Compute available criteria options based on the selected column
          let availableCriteria = filterRegistryCriteriaForColumn(selectedColumn);
          if (availableCriteria.length === 0 && selectedColumn && isModelArrayDataType(selectedColumn.dataType)) {
            availableCriteria = [Criterion.containsAny, Criterion.containsAll, Criterion.notContainsAny, Criterion.notContainsAll];
          } else if (availableCriteria.length === 0 && selectedColumn?.dataType) {
            const dataType = selectedColumn.dataType;
            if (dataType in criteriaMapping) {
              availableCriteria = criteriaMapping[dataType as keyof typeof criteriaMapping];
            } else if (isModelDataType(dataType)) {
              availableCriteria = criteriaMapping.relation;
            } else {
              availableCriteria = [Criterion.is];
            }
          } else if (availableCriteria.length === 0) {
            availableCriteria = [Criterion.is];
          }

          // Set criterion to first option if available
          if (availableCriteria.length > 0) {
            const firstCriterion = availableCriteria[0];
            rule.value.criterion = firstCriterion.value;
            selectedCriterionConfig = firstCriterion;
          }
        } else {
          // Criterion already exists, find its config to check if value should be auto-selected
          // Compute available criteria options to find the current criterion config
          let availableCriteria = filterRegistryCriteriaForColumn(selectedColumn);
          if (availableCriteria.length === 0 && selectedColumn && isModelArrayDataType(selectedColumn.dataType)) {
            availableCriteria = [Criterion.containsAny, Criterion.containsAll, Criterion.notContainsAny, Criterion.notContainsAll];
          } else if (availableCriteria.length === 0 && selectedColumn?.dataType) {
            const dataType = selectedColumn.dataType;
            if (dataType in criteriaMapping) {
              availableCriteria = criteriaMapping[dataType as keyof typeof criteriaMapping];
            } else if (isModelDataType(dataType)) {
              availableCriteria = criteriaMapping.relation;
            } else {
              availableCriteria = [Criterion.is];
            }
          } else if (availableCriteria.length === 0) {
            availableCriteria = [Criterion.is];
          }

          selectedCriterionConfig = availableCriteria.find(
            (criterionOption: CriterionConfig) => criterionOption.value === currentCriterion
          );
        }
        
        // Find criterion config if not already found (needed for UI to show correct control type)
        if (!selectedCriterionConfig && currentCriterion) {
          const availableCriteria = getAvailableCriteriaForColumn(selectedColumn);
          selectedCriterionConfig = availableCriteria.find(
            (criterionOption: CriterionConfig) => criterionOption.value === currentCriterion
          );
        }
        
        // Auto-select first boolean value if criterion is boolean and value is empty (and wasn't set by defaults)
        if (selectedCriterionConfig?.control === 'boolean') {
          const currentValue = rule.value.value;
          if ((currentValue === null || currentValue === undefined || currentValue === '') && !defaultValueApplied) {
            rule.value.value = booleanOptions[0].value; // true
          }
        }
      }
    }
  },
});

const criterion = computed({
  get: () => {
    return rule.value.criterion;
  },
  set: (criterionValue) => {
    rule.value.criterion = criterionValue;

    if (criterionValue) {
      const criterionConfig = criteriaOptions.value.find(
        (criterionOption: CriterionConfig) => criterionOption.value === criterionValue
      );
      if (criterionConfig?.control === 'boolean') {
        const currentValue = rule.value.value;
        if (currentValue === null || currentValue === undefined || currentValue === '') {
          rule.value.value = booleanOptions[0].value; // true
        }
      }
      if (criterionConfig?.control === 'multi_combobox') {
        const currentValue = rule.value.value;
        if (currentValue === null || currentValue === undefined || currentValue === '') {
          rule.value.value = [];
        } else if (!Array.isArray(currentValue)) {
          rule.value.value = [String(currentValue)];
        }
      }
      // Switching from multi-select (e.g. "is one of") back to scalar (e.g. "is")
      // must collapse the array down to a single string so the backend keeps
      // receiving a canonical enum value rather than an unexpected array.
      if (criterionConfig?.control === 'single_select') {
        const currentValue = rule.value.value;
        if (Array.isArray(currentValue)) {
          rule.value.value = currentValue.length > 0 ? String(currentValue[0]) : null;
        } else if (currentValue === undefined || currentValue === '') {
          rule.value.value = null;
        }
      }
      // A range starts from the date already picked. Leaving a range for a one-date
      // operator keeps its start.
      if (criterionConfig?.control === 'date_range') {
        rule.value.value = dateRange(rule.value.value);
      }
      if (criterionConfig?.control === 'date' && Array.isArray(rule.value.value)) {
        rule.value.value = rule.value.value[0] ?? null;
      }
      if (criterionConfig?.control === 'id_list') {
        rule.value.value = idListFromRuleValue(rule.value.value);
      }
      // Leaving "is one of" for "is equal to" keeps the first id rather than
      // sending an array to an operator that expects one number. Only `number`:
      // the model-array operators (contains any / all, ...) carry `text` here and
      // only become model_array in currentCriterion, so collapsing on `text`
      // would cut a multi-tag selection down to its first tag.
      if (criterionConfig?.control === 'number') {
        const currentValue = rule.value.value;
        if (Array.isArray(currentValue)) {
          rule.value.value = currentValue.length > 0 ? currentValue[0] : null;
        }
      }
      // For is_empty_or_null / is_not_empty_or_null, value must be null (or omitted) per API
      if (criterionConfig?.control === 'none') {
        rule.value.value = null;
      }
    }
  },
});

const ruleValue = computed({
  get: () => rule.value.value,
  set: (value) => (rule.value.value = value),
});

const selectedColumn = computed(() => {
  return props.columns.find((col) => col.key === column.value);
});

/**
 * The columns this row's picker offers. A column hidden from the picker is offered only in a
 * row whose rule already uses it, so a saved rule still names its column.
 */
const columnOptions = computed(() =>
  props.columns.filter((tableColumn) => !tableColumn.hiddenFromFilterPicker || tableColumn.key === column.value),
);

/** The custom list search heads its columns "Custom List" and "Schedule". Other entities list them flat. */
const columnsHaveGroups = computed(() => props.columns.some((tableColumn) => tableColumn.filterGroup !== undefined));
function columnGroup(tableColumn: TableColumn): string {
  return tableColumn.filterGroup ?? '';
}

const selectedColumnHeader = computed(() => {
  return selectedColumn.value?.header || '';
});

const columnDataType = computed(() => {
  return selectedColumn.value?.dataType;
});

const resolvedFilterOptionsSource = computed(() =>
  resolveFilterControlOptionsSource(selectedColumn.value)
);

const filterControlRegistryEntry = computed(() => {
  const slug = resolvedFilterOptionsSource.value;
  return slug ? filterControlRegistry[slug] ?? null : null;
});

const filterComboPlaceholder = computed(() => {
  return selectedColumn.value?.filterControl?.placeholder ?? 'Select...';
});

const isModelArrayField = computed(() => {
  const dataType = columnDataType.value;
  return dataType ? isModelArrayDataType(dataType) : false;
});

const entityStore = useEntityStore();
const currentModelName = computed(() => {
  return entityStore.selectedEntityId;
});

const modelArraySlug = computed(() => {
  if (!isModelArrayField.value || !selectedColumn.value || !currentModelName.value) {
    return null;
  }
  const fieldName = selectedColumn.value.key;
  return getFilterArraySlug(currentModelName.value, fieldName);
});

const criteriaOptions = computed(() => {
  const col = selectedColumn.value;
  if (!col) return [Criterion.is];

  // Use the same logic as getAvailableCriteriaForColumn so nullable number/Date get is empty / is not empty
  return getAvailableCriteriaForColumn(col);
});

const currentCriterion = computed(() => {
  let matchedCriterionConfig =
    criteriaOptions.value.find(
      (criterionOption: CriterionConfig) => criterionOption.value === criterion.value
    ) || Criterion.isEqualTo;

  // The registry-driven criteria already carry the correct `control` for each
  // operator (severity / issue_type / original_status use `single_select` for
  // is / is not and `multi_combobox` for is one of / is none of). Trust the
  // registry value and only override for model-array fields, which still need
  // the model-array combobox when the operator is an array operator.
  if (
    isModelArrayField.value &&
    modelArraySlug.value &&
    (criterion.value === 'contains_any' ||
      criterion.value === 'contains_all' ||
      criterion.value === 'not_contains_any' ||
      criterion.value === 'not_contains_all')
  ) {
    matchedCriterionConfig = { ...matchedCriterionConfig, control: 'model_array' };
  }

  return matchedCriterionConfig;
});

// Labels only — the rule value stays a real boolean, which is what the API expects.
const booleanOptions = [
  { label: BOOLEAN_YES_LABEL, value: true },
  { label: BOOLEAN_NO_LABEL, value: false },
];

// For model arrays, we need to handle array of IDs
const modelArrayValue = computed({
  get: () => {
    if (Array.isArray(ruleValue.value)) {
      return ruleValue.value
        .map((rawValue) => (typeof rawValue === 'number' ? rawValue : Number(rawValue)))
        .filter((numericValue) => !isNaN(numericValue));
    }
    // If it's a single number, convert to array
    if (typeof ruleValue.value === 'number') {
      return [ruleValue.value];
    }
    // If it's a string, try to parse it
    if (typeof ruleValue.value === 'string' && ruleValue.value) {
      const parsed = Number(ruleValue.value);
      return !isNaN(parsed) ? [parsed] : [];
    }
    // Return empty array when no value is set
    return [];
  },
  set: (value: number[]) => {
    // Store as array for multi-select
    ruleValue.value = value.length === 1 ? value[0] : value;
  }
});

// Scalar value for single-select enum filter rules ("is" / "is not").
// Always coerces the underlying rule value into a single string so the backend
// keeps receiving a canonical enum string regardless of how the user moved
// between scalar and multi operators.
const singleSelectValue = computed<string | null>({
  get: () => {
    const currentValue = ruleValue.value;
    if (Array.isArray(currentValue)) {
      return currentValue.length > 0 ? String(currentValue[0]) : null;
    }
    if (currentValue === null || currentValue === undefined || currentValue === '') {
      return null;
    }
    return String(currentValue);
  },
  set: (value: string | null) => {
    ruleValue.value = value;
  },
});

// For configurable filter control (multi-combobox), same array-of-strings value shape
const multiComboboxValue = computed({
  get: () => {
    if (Array.isArray(ruleValue.value)) {
      return ruleValue.value.map(String);
    }
    if (typeof ruleValue.value === 'string' && ruleValue.value) {
      return [ruleValue.value];
    }
    return [];
  },
  set: (value: string[]) => {
    ruleValue.value = value.length === 1 ? value[0] : value;
  }
});

/** A "between" rule's two ends, from and to. A single date becomes the from. */
function dateRange(value: unknown): [string | null, string | null] {
  if (Array.isArray(value)) {
    return [value[0] ?? null, value[1] ?? null];
  }
  return [typeof value === 'string' && value !== '' ? value : null, null];
}

// A From moved past the To clears the To. The To picker then starts at the new From, so
// the user picks an end that fits rather than keeping a range that matches nothing.
const rangeFromValue = computed<string | null>({
  get: () => dateRange(ruleValue.value)[0],
  set: (from) => {
    const to = dateRange(ruleValue.value)[1];
    ruleValue.value = [from ?? null, isEndBeforeStart(from, to) ? null : to];
  },
});

const rangeToValue = computed<string | null>({
  get: () => dateRange(ruleValue.value)[1],
  set: (to) => {
    ruleValue.value = [dateRange(ruleValue.value)[0], to ?? null];
  },
});

const idListValue = computed<number[]>({
  get: () => idListFromRuleValue(ruleValue.value),
  set: (ids) => {
    ruleValue.value = ids;
  },
});

const filterOptionsSlug = computed(() => resolvedFilterOptionsSource.value);
const { data: filterOptionsData, isLoading: filterOptionsLoading } = useFilterOptions(filterOptionsSlug);
const filterOptionsList = computed(() => filterOptionsData.value ?? []);

// --- Favorite filters (SAU-260) ---------------------------------------------

const { isFavorited, isFavoritePending, toggleFavorite } = useFavoriteFilters();

// Labels for the model-array selection, kept in sync by ModelArrayCombobox's
// `update:selectedLabels` emit so starring can snapshot human names for
// id-valued fields (with a raw-id fallback while options load).
const modelArraySelectedLabels = ref<string[]>([]);

const ruleIsFavoritable = computed(() =>
  isFavoritableRule(rule.value, currentCriterion.value.control ?? null)
);

// Fingerprint match against the saved favorites: editing a starred row so it
// no longer matches simply renders the star outlined — the favorite itself is
// untouched, no extra state needed.
const ruleIsFavorited = computed(() => ruleIsFavoritable.value && isFavorited(rule.value));

// While the favorite's create POST is still in flight (optimistic row, no
// real id yet) the star is disabled: un-starring would have to DELETE a
// placeholder id that doesn't exist server-side.
const ruleFavoritePending = computed(
  () => ruleIsFavorited.value && isFavoritePending(rule.value),
);

const favoriteToggleTitle = computed(() => {
  if (!ruleIsFavoritable.value) {
    return 'Complete the filter to favorite it';
  }
  if (ruleFavoritePending.value) {
    return 'Saving favorite…';
  }
  return ruleIsFavorited.value ? 'Remove from favorites' : 'Save as favorite';
});

/** Human-readable labels for the rule's value(s), used in the favorite's label. */
const favoriteValueLabels = computed<string[]>(() => {
  const control = currentCriterion.value.control;
  if (control === 'none') {
    return [];
  }
  const rawValue = ruleValue.value;
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return [];
  }
  if (control === 'boolean') {
    return [booleanDisplayString(rawValue === true)];
  }
  if (control === 'date_range') {
    return [dateRangeValueLabel(rawValue)];
  }
  if (control === 'model_array') {
    if (modelArraySelectedLabels.value.length > 0) {
      return modelArraySelectedLabels.value;
    }
    return (Array.isArray(rawValue) ? rawValue : [rawValue]).map(String);
  }
  const values = Array.isArray(rawValue) ? rawValue : [rawValue];
  const registryEntry = filterControlRegistryEntry.value;
  if (registryEntry && (control === 'multi_combobox' || control === 'single_select')) {
    return values.map((valueEntry) => {
      const option = filterOptionsList.value.find(
        (candidate) => registryEntry.getValue(candidate) === String(valueEntry),
      );
      return option ? registryEntry.getLabel(option) : String(valueEntry);
    });
  }
  return values.map(String);
});

function onToggleFavorite() {
  if (!ruleIsFavoritable.value || ruleFavoritePending.value) {
    return;
  }
  void toggleFavorite(
    {
      column: rule.value.column,
      field: rule.value.field ?? null,
      criterion: rule.value.criterion,
      value: rule.value.value,
    },
    buildFavoriteLabel({
      columnHeader: selectedColumnHeader.value,
      criterionLabel: currentCriterion.value.label,
      valueLabels: favoriteValueLabels.value,
    }),
  );
}
</script>

<template>
  <!--
    Notion-style rule cells.

    `display: contents` makes this wrapper invisible to layout so the four
    cells become direct grid items of the parent FilterBody subgrid row
    (`.draggable-item`). Each cell is placed in a named column tracked by
    the level grid — `field` and `op` are `auto` tracks so the widest
    cell across every rule in the level dictates the width for all of
    them. The result: every column control in a level shares one width,
    every operator control in a level shares one width.

    The parent FilterBody owns the prefix cell (Where / And / dropdown).
  -->
  <div class="contents" data-testid="filter-rule-row">
    <div style="grid-column: field-start / field-end;" class="min-w-0">
      <Select
        v-model="column"
        v-model:open="columnSelectOpen"
        :options="columnOptions"
        optionLabel="header"
        optionValue="key"
        :optionGroup="columnsHaveGroups ? columnGroup : undefined"
        placeholder="Select a Column"
        size="sm"
        data-testid="filter-column-select"
        class="text-xs w-full"
        :title="selectedColumnHeader || undefined"
        tabindex="0"
      />
    </div>
    <div style="grid-column: op-start / op-end;" class="min-w-0">
      <Select
        v-model="criterion"
        :options="criteriaOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Select a Criteria"
        size="sm"
        data-testid="filter-criterion-select"
        class="text-xs w-full"
        :title="currentCriterion?.label || undefined"
        tabindex="0"
      />
    </div>
    <!--
      Value cell. Renders a placeholder when the operator has no value
      control ("is empty" / "is not empty") so the menu cell stays in the
      menu column instead of collapsing leftward.
    -->
    <div style="grid-column: value-start / value-end;" class="min-w-0 w-full">
      <div v-if="currentCriterion.control === 'none'" aria-hidden="true"></div>
      <Input
        v-else-if="currentCriterion.control === 'number'"
        v-model="ruleValue"
        data-testid="filter-number-input"
        class="min-w-0 w-full h-8 text-xs"
        tabindex="0"
      />
      <!--
        Keyed by column so a column change starts from an empty box. The input
        keeps the typed text separately from the rule value, and text holding no
        ids ("abc") parses to the same [] the reset writes, so without the key
        it would survive onto the new column.
      -->
      <FilterIdListInput
        v-else-if="currentCriterion.control === 'id_list'"
        :key="column"
        v-model="idListValue"
      />
      <DatePicker
        v-else-if="currentCriterion.control === 'date'"
        v-model="ruleValue"
        data-testid="filter-date-picker"
        class="min-w-0 max-w-none h-8 text-xs"
      />
      <!-- The two dates are compact: no clear or calendar button, and clicking a date opens
           its calendar. Each is as wide as its digits, so both fit beside "and" on one line.
           A row narrower than that wraps the second date onto its own line. -->
      <div
        v-else-if="currentCriterion.control === 'date_range'"
        class="flex min-w-0 w-full flex-wrap items-center gap-1"
        data-testid="filter-date-range"
      >
        <DatePicker
          v-model="rangeFromValue"
          label="From"
          compact
          data-testid="filter-date-range-from"
          class="w-[6.5rem] max-w-full h-8 text-xs"
        />
        <span class="text-xs text-muted-foreground">and</span>
        <DatePicker
          v-model="rangeToValue"
          :min="rangeFromValue"
          label="To"
          compact
          data-testid="filter-date-range-to"
          class="w-[6.5rem] max-w-full h-8 text-xs"
        />
      </div>
      <Select
        v-else-if="currentCriterion.control === 'boolean'"
        :options="booleanOptions"
        v-model="ruleValue"
        optionLabel="label"
        optionValue="value"
        placeholder="Select value"
        size="sm"
        data-testid="filter-boolean-select"
        class="min-w-0 w-full text-xs"
        tabindex="0"
      />
      <FilterMultiCombobox
        v-else-if="currentCriterion.control === 'multi_combobox' && filterControlRegistryEntry"
        v-model="multiComboboxValue"
        :options="filterOptionsList"
        :get-value="filterControlRegistryEntry.getValue"
        :get-label="filterControlRegistryEntry.getLabel"
        :filter-by-query="filterControlRegistryEntry.filterByQuery"
        :option-component="filterControlRegistryEntry.optionComponent"
        :is-loading="filterOptionsLoading"
        :placeholder="filterComboPlaceholder"
        test-id-prefix="filter-multi-combobox"
      />
      <FilterEnumSelect
        v-else-if="currentCriterion.control === 'single_select' && filterControlRegistryEntry"
        v-model="singleSelectValue"
        :options="filterOptionsList"
        :get-value="filterControlRegistryEntry.getValue"
        :get-label="filterControlRegistryEntry.getLabel"
        :option-component="filterControlRegistryEntry.optionComponent"
        :placeholder="filterComboPlaceholder"
        test-id-prefix="filter-enum-select"
      />
      <ModelArrayCombobox
        v-else-if="currentCriterion.control === 'model_array' && modelArraySlug"
        v-model="modelArrayValue"
        :slug="modelArraySlug"
        data-testid="filter-model-array-combobox"
        @update:selected-labels="modelArraySelectedLabels = $event"
      />
      <Input
        v-else
        v-model="ruleValue"
        type="text"
        placeholder="Enter value"
        data-testid="filter-text-input"
        class="min-w-0 w-full h-8 text-xs"
        tabindex="0"
      />
    </div>
    <!--
      On mobile the menu cell sits in its own row; `justify-end` pushes the
      icon buttons to the right edge of the rule. On `md+` the parent grid
      places the cell in the `menu` column and `justify-self-end` snugs it
      against the right of that column.
    -->
    <div style="grid-column: menu-start / menu-end;" class="flex gap-0.5 justify-end md:justify-self-end">
      <!--
        Favorite star (SAU-260). Disabled + dimmed with a teaching tooltip on
        incomplete rows (never hidden, so the layout doesn't jump); outline =
        not saved, solid amber = saved.
      -->
      <LazyTooltip>
        <!--
          The span is the tooltip's trigger. A disabled button takes no pointer
          events, so without it the teaching tooltip on an incomplete row could
          never open.
        -->
        <span class="inline-flex shrink-0">
          <Button
            size="icon"
            :aria-label="favoriteToggleTitle"
            :aria-pressed="ruleIsFavorited"
            :disabled="!ruleIsFavoritable || ruleFavoritePending"
            class="shrink-0 h-7 w-7"
            :class="!ruleIsFavoritable ? 'opacity-40' : 'text-muted-foreground hover:text-foreground'"
            variant="ghost"
            data-testid="filter-favorite-toggle-button"
            :data-favorited="ruleIsFavorited ? 'true' : 'false'"
            :data-favorite-pending="ruleFavoritePending ? 'true' : undefined"
            @click="onToggleFavorite"
          >
            <StarIconSolid v-if="ruleIsFavorited" class="h-3.5 w-3.5 text-amber-500" />
            <StarIconOutline v-else class="h-3.5 w-3.5" />
          </Button>
        </span>
        <template #content>{{ favoriteToggleTitle }}</template>
      </LazyTooltip>
      <LazyTooltip v-if="depth < 4">
        <Button
          size="icon"
          aria-label="Add Group"
          class="shrink-0 h-7 w-7"
          @click="$emit('addInnerRule', rule.id)"
          variant="ghost"
          data-testid="filter-add-group-button"
        >
          <Squares2X2Icon class="h-3.5 w-3.5" />
        </Button>
        <template #content>Add group</template>
      </LazyTooltip>
      <LazyTooltip>
        <Button
          size="icon"
          aria-label="Delete rule"
          @click="$emit('deleteRule', rule.id)"
          class="shrink-0 h-7 w-7 text-muted-foreground hover:text-destructive"
          variant="ghost"
          data-testid="filter-delete-rule-button"
        >
          <TrashIcon class="h-3.5 w-3.5" />
        </Button>
        <template #content>Delete rule</template>
      </LazyTooltip>
    </div>
  </div>
</template>
